import React, { useState, useEffect, useRef, TouchEvent } from 'react';
import styles from './FeaturedScenarioCarouselMobile.module.css';
import { ScenarioDefinitionListResponse } from '../../api/types/scenario';

interface FeaturedScenarioCarouselMobileProps {
  featuredScenarios: ScenarioDefinitionListResponse[];
  onScenarioClick: (scenario: ScenarioDefinitionListResponse) => void;
}

const FeaturedScenarioCarouselMobile: React.FC<FeaturedScenarioCarouselMobileProps> = ({ 
  featuredScenarios = [], // Provide default empty array 
  onScenarioClick 
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const rotationIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const resumeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const ROTATION_INTERVAL = 5000; // 5 seconds
  const RESUME_AFTER_INTERACTION = 10000; // Resume auto-rotation after 10 seconds of inactivity
  
  // Add state to track if auto-rotation should be paused
  const [isPaused, setIsPaused] = useState(false);
  
  // Touch handling
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [isSwiping, setIsSwiping] = useState(false);
  
  // Minimum swipe distance (in px)
  const minSwipeDistance = 30; // Reduced from 50 to make swiping more responsive

  // Reset active index if the featured scenarios change
  useEffect(() => {
    if (activeIndex >= featuredScenarios.length && featuredScenarios.length > 0) {
      setActiveIndex(0);
    }
  }, [featuredScenarios, activeIndex]);

  // Function to pause auto-rotation and set a timeout to resume it
  const pauseAndScheduleResume = () => {
    setIsPaused(true);
    
    // Clear any existing resume timeout
    if (resumeTimeoutRef.current) {
      clearTimeout(resumeTimeoutRef.current);
    }
    
    // Set a timeout to resume auto-rotation after inactivity
    resumeTimeoutRef.current = setTimeout(() => {
      setIsPaused(false);
    }, RESUME_AFTER_INTERACTION);
  };

  // Auto-rotate the carousel every 5 seconds
  useEffect(() => {
    if (featuredScenarios.length <= 1) return;
    
    // Clear any existing intervals
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
    }
    
    if (rotationIntervalRef.current) {
      clearInterval(rotationIntervalRef.current);
    }
    
    // Reset progress
    setProgress(0);
    
    // Only create rotation interval if not paused
    if (!isPaused) {
      // Create rotation interval
      rotationIntervalRef.current = setInterval(() => {
        setActiveIndex((current) => (current + 1) % featuredScenarios.length);
        setProgress(0); // Reset progress when changing slides
      }, ROTATION_INTERVAL);
    }
    
    return () => {
      if (rotationIntervalRef.current) {
        clearInterval(rotationIntervalRef.current);
      }
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
      if (resumeTimeoutRef.current) {
        clearTimeout(resumeTimeoutRef.current);
      }
    };
  }, [featuredScenarios.length, isPaused]);

  // Handle manual navigation
  const goToSlide = (index: number) => {
    setActiveIndex(index);
    setProgress(0); // Reset progress when manually changing slides
    pauseAndScheduleResume(); // Pause auto-rotation when user manually navigates
  };
  
  // Touch event handlers
  const onTouchStart = (e: TouchEvent) => {
    // Immediately pause auto-rotation when user starts touching
    pauseAndScheduleResume();
    setIsSwiping(true);
    setTouchEnd(null); // Reset touchEnd
    setTouchStart(e.targetTouches[0].clientX);
    
    // Prevent default behavior to avoid scrolling
    e.preventDefault();
  };
  
  const onTouchMove = (e: TouchEvent) => {
    if (!touchStart) return;
    setTouchEnd(e.targetTouches[0].clientX);
    
    // Prevent default to avoid scrolling while swiping
    e.preventDefault();
  };
  
  const onTouchEnd = (e: TouchEvent) => {
    if (!touchStart || !touchEnd) {
      setIsSwiping(false);
      return;
    }
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    // Handle swipe if it meets the minimum distance
    if (isLeftSwipe && featuredScenarios.length > 1) {
      // Swipe left - go to next slide
      setActiveIndex((current) => (current + 1) % featuredScenarios.length);
    } else if (isRightSwipe && featuredScenarios.length > 1) {
      // Swipe right - go to previous slide
      setActiveIndex((current) => 
        current === 0 ? featuredScenarios.length - 1 : current - 1
      );
    }
    
    // Reset touch positions
    setTouchStart(null);
    setTouchEnd(null);
    setIsSwiping(false);
    
    // Prevent default behavior
    e.preventDefault();
  };

  // Return null if there are no scenarios to display
  if (!featuredScenarios || !featuredScenarios.length) return null;

  // Make sure we have a valid active index
  const safeActiveIndex = Math.min(activeIndex, featuredScenarios.length - 1);
  const activeScenario = featuredScenarios[safeActiveIndex];

  // Additional safety check
  if (!activeScenario) return null;

  // Calculate indices for the carousel cards - similar to desktop version
  const getVisibleScenarios = () => {
    const result = [];
    const totalScenarios = featuredScenarios.length;
    
    // Show 3 cards on mobile (instead of 5 on desktop)
    const maxVisibleCards = 3;
    const maxCards = Math.min(maxVisibleCards, totalScenarios);
    
    for (let i = 0; i < maxCards; i++) {
      // Calculate position relative to active index
      let position = (safeActiveIndex + i - Math.floor(maxCards / 2) + totalScenarios) % totalScenarios;
      result.push({
        scenario: featuredScenarios[position],
        position: i - Math.floor(maxCards / 2) // -1, 0, 1
      });
    }
    
    return result;
  };

  const visibleScenarios = getVisibleScenarios();

  return (
    <div className={styles.carouselContainer}>
      <div className={styles.headerRow}>
        <h2 className={styles.sectionTitle}>Featured Scenarios</h2>
      </div>
      
      <div 
        className={`${styles.carouselWrapper} ${isSwiping ? styles.swiping : ''}`}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {/* Star decorations - like desktop */}
        <div className={styles.starDecoration1}></div>
        <div className={styles.starDecoration2}></div>
        <div className={styles.starDecoration3}></div>
        <div className={styles.starDecoration4}></div>
        
        {/* Carousel cards - using desktop-like approach */}
        <div className={styles.cardsContainer}>
          {visibleScenarios.map(({ scenario, position }) => (
            <div 
              key={scenario.id} 
              className={`${styles.card} ${position === 0 ? styles.activeCard : ''} ${styles[`position${position}`]}`}
              onClick={() => {
                goToSlide((safeActiveIndex + position + featuredScenarios.length) % featuredScenarios.length);
                if (position === 0) {
                  onScenarioClick(scenario);
                }
              }}
            >
              <div 
                className={styles.cardImage}
                style={{ backgroundImage: `url(${scenario.icon || '/media/default-scenario.png'})` }}
              ></div>
              <div className={styles.cardContent}>
                <h3 className={styles.cardTitle}>{scenario.display_name || 'Untitled'}</h3>
                {position === 0 && (
                  <div className={styles.playButton}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="12" cy="12" r="11" stroke="white" strokeWidth="2"/>
                      <path d="M16 12L10 16V8L16 12Z" fill="white"/>
                    </svg>
                    <span>Start</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FeaturedScenarioCarouselMobile; 